import { useMutation, useQuery } from '@tanstack/react-query';
import { call } from '@/lib/api';
import { queryClient } from '@/lib/query';
import { referringAgentKeys } from '@/models/referringAgents';

export type LeadStatus =
  | 'New Lead'
  | 'Contact Attempt'
  | 'Meeting Scheduled'
  | 'Meeting Complete'
  | 'Proposal Sent'
  | '2nd Meeting +'
  | 'Deal Opportunity'
  | 'Lost'
  | 'Won'
  | 'converted';
export type LeadSqlStatus = 'Qualified' | 'Disqualified';
export type LeadMqlStatus =
  | 'Qualified'
  | 'Disqualified'
  | 'Competitor/Agent'
  | 'Non-marketing';
export type LeadKind = 'client' | 'agent';
export type LeadSource =
  | 'Telegram'
  | 'Skype'
  | 'Whatsapp'
  | 'Referral'
  | 'Contact-Form'
  | 'In-Call'
  | 'Email'
  | 'Other'
  | 'Chat'
  | 'LinkedIn'
  | 'Manual'
  | 'Meta Ads';
export type LeadEmailStatus = 'unknown' | 'valid' | 'bounced';

export const LEAD_STATUSES: LeadStatus[] = [
  'New Lead',
  'Contact Attempt',
  'Meeting Scheduled',
  'Meeting Complete',
  'Proposal Sent',
  '2nd Meeting +',
  'Deal Opportunity',
  'Lost',
  'Won',
];
export const MANUAL_LEAD_STATUSES: LeadStatus[] = [...LEAD_STATUSES];
export const LEAD_SQL_STATUSES: LeadSqlStatus[] = ['Qualified', 'Disqualified'];
export const LEAD_MQL_STATUSES: LeadMqlStatus[] = [
  'Qualified',
  'Disqualified',
  'Competitor/Agent',
  'Non-marketing',
];
export const ARCHIVE_REASON_LABELS: Record<string, string> = {
  converted_before_merge: 'converted before the merge',
  stale_at_migration: 'no activity at migration',
  monday_disqualified: 'disqualified in Monday',
  old_board: 'old Monday board',
};
export const LEAD_SOURCES: LeadSource[] = [
  'Telegram',
  'Skype',
  'Whatsapp',
  'Referral',
  'Contact-Form',
  'In-Call',
  'Email',
  'Other',
  'Chat',
  'LinkedIn',
  'Manual',
  'Meta Ads',
];

export const BUSINESS_MODELS: string[] = [
  'Banking License (PI, EMI)',
  'Crypto',
  'Gaming/Gambling',
  'Forex, Investment',
  'Banking',
  'Company Formation',
];

export type Lead = {
  id: number;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  whatsapp: string | null;
  telegram: string | null;
  source: LeadSource;
  /** IS-106: the original source selection, verbatim. Empty for leads imported before it was kept. */
  sourceLabels: string[];
  sourceDetail: string | null;
  referringAgentId: number | null;
  referringAgentName?: string | null;
  assigneeUserId: number | null;
  assigneeEmail?: string | null;
  assigneeInfo?: { name?: string; picture?: string } | null;
  status: LeadStatus;
  emailStatus: LeadEmailStatus;
  lastContactAt: string | null;
  request: string | null;
  extendedMessage: string | null;
  createdAt: string;
  updatedAt: string;
  contactId: number | null;
  website: string | null;
  businessModel: string | null;
  originUrl: string | null;
  path: string | null;
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  marketingCampaign: string | null;
  marketingChannel: string | null;
  affiliate: string | null;
  affiliateSource: string | null;
  ip: string | null;
  gdprConsent: boolean | null;
  mondayItemId: number | null;
  reason?: string | null;
  nextTaskAt?: string | null;
  mqlStatus?: LeadMqlStatus | null;
  sqlStatus?: LeadSqlStatus | null;
  qualifiedAt?: string | null;
  qualifiedBy?: number | null;
  kind?: LeadKind;
  isSpam?: boolean;
  archivedAt?: string | null;
  archiveReason?: string | null;
  duplicateOfLeadId?: number | null;
};

export type LeadComment = {
  id: number;
  leadId: number;
  authorUserId: number | null;
  authorEmail: string | null;
  body: string;
  createdAt: string;
};

export type LeadEvent = {
  id: number;
  leadId: number;
  actorUserId: number | null;
  kind:
    | 'created'
    | 'status_changed'
    | 'reason_changed'
    | 'assignee_changed'
    | 'field_updated'
    | 'commented'
    | 'converted'
    | 'qualified'
    | 'stage_auto';
  payload: Record<string, unknown>;
  createdAt: string;
  actorEmail?: string | null;
  actorName?: string | null;
  assigneeFromName?: string | null;
  assigneeToName?: string | null;
};

export type LeadAssignee = {
  id: number;
  email: string;
  info?: { name?: string; picture?: string } | null;
};

export type LeadListFilters = {
  status?: LeadStatus[];
  ownerUserId?: number[];
  source?: LeadSource[];
  search?: string;
  view?: 'mine' | 'unassigned' | 'all';
  page?: number;
  pageSize?: number;
  sort?: 'createdAt' | 'updatedAt' | 'lastContactAt';
  order?: 'asc' | 'desc';
};

export type LeadListResponse = {
  rows: Lead[];
  total: number;
  page: number;
  pageSize: number;
};
export type LeadClient = { id: number; name: string; visible: boolean };

export type PersonLevel =
  | 'all'
  | 'member'
  | 'unassigned'
  | 'open_assignee'
  | 'closed_assignee'
  | 'none';

export type PersonRequest = {
  id?: number;
  status?: LeadStatus;
  reason?: string | null;
  sqlStatus?: LeadSqlStatus | null;
  mqlStatus?: LeadMqlStatus | null;
  source?: LeadSource;
  sourceDetail?: string | null;
  mondayMarketingItemId?: number | null;
  request?: string | null;
  assigneeUserId?: number | null;
  assigneeName?: string | null;
  assigneeEmail?: string | null;
  createdAt: string;
  archivedAt?: string | null;
  archiveReason?: string | null;
  isSpam?: boolean;
  duplicateOfLeadId?: number | null;
};

export type PersonComment = {
  target: 'lead' | 'contact';
  leadId: number | null;
  id: number;
  body: string;
  createdAt: string;
  authorEmail: string | null;
  authorName: string | null;
};

export type PersonBlock = {
  id: number;
  level: PersonLevel;
  isMember: boolean;
  hasMembers: boolean;
  canRequestAccess: boolean;
  assigneeIsMember: boolean;
  repeat: boolean;
  header: {
    id: number;
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    phone: string | null;
    whatsapp: string | null;
    telegram: string | null;
    qualified: boolean;
    client: boolean;
    lastContactAt: string | null;
  };
  requests: PersonRequest[];
  comments: PersonComment[];
  events: LeadEvent[];
};

export type LeadDetailsResponse = {
  lead: Lead;
  contact: LeadClient | null;
  person: PersonBlock | null;
  access: { write: boolean };
  comments: LeadComment[];
  events: LeadEvent[];
};

export type DuplicateMatch = {
  entity: 'lead' | 'contact';
  id: number | null;
  hidden?: boolean;
  ownerName?: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  status: LeadStatus | null;
  contactId?: number | null;
};

export type DuplicateCheckResult = {
  exact: DuplicateMatch[];
  similar: DuplicateMatch[];
  target: (LeadClient & { kind: 'contact' }) | null;
};

export type MappedMondayLead = Pick<
  Lead,
  | 'firstName'
  | 'lastName'
  | 'email'
  | 'phone'
  | 'whatsapp'
  | 'telegram'
  | 'source'
  | 'sourceLabels'
  | 'sourceDetail'
  | 'status'
  | 'emailStatus'
  | 'request'
  | 'extendedMessage'
  | 'website'
  | 'businessModel'
  | 'originUrl'
  | 'path'
  | 'utmSource'
  | 'utmMedium'
  | 'utmCampaign'
  | 'marketingCampaign'
  | 'marketingChannel'
  | 'affiliate'
  | 'affiliateSource'
  | 'ip'
  | 'gdprConsent'
  | 'lastContactAt'
>;

export type MondayLeadImportCandidate = {
  mondayItemId: number;
  itemName: string;
  lead: MappedMondayLead;
  assigneeUserId: number | null;
  assigneeMondayEmail: string | null;
  referringAgent: { name: string; existing: boolean } | null;
  unmapped: {
    source?: string;
    status?: string;
    businessModel?: string;
    extraReferringAgents?: string[];
  };
  duplicates: { kind: 'lead' | 'contact'; on: 'email' | 'phone'; id: number }[];
  missing: string[];
  existingLeadId: number | null;
  lineage: {
    mondayMarketingItemId: number | null;
    mondayContactItemId: number | null;
  };
  lineageWarnings: string[];
  placement: LeadPlacement;
};

export type LeadPlacement =
  | { kind: 'existing'; leadId: number }
  | {
      kind: 'contact';
      contactId: number;
      contactName: string;
      via: 'monday_contact' | 'email';
      visible: boolean;
    }
  | { kind: 'refused'; leadId: number }
  | { kind: 'new' };

export const leadKeys = {
  all: ['leads'] as const,
  lists: () => [...leadKeys.all, 'list'] as const,
  list: (f: LeadListFilters) => [...leadKeys.lists(), f] as const,
  details: () => [...leadKeys.all, 'detail'] as const,
  detail: (id: number) => [...leadKeys.details(), Number(id)] as const,
  assignees: ['leads', 'assignees'] as const,
};

const callGet = <T>(endpoint: string, json?: any): Promise<T> =>
  call<T>(endpoint, { method: 'GET', json }).then((r) => r.json());

const callPost = <T>(endpoint: string, json?: any): Promise<T> =>
  call<T>(endpoint, { json }).then((r) => r.json());

// Server expects comma-separated lists for repeatable filter params. The codebase's
// default `objectToSearchParams` appends one entry per array item; serialize manually
// so the backend's csv() helper gets a single comma-joined string.
function serializeFilters(f: LeadListFilters): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (f.status?.length) out.status = f.status.join(',');
  if (f.ownerUserId?.length) out.ownerUserId = f.ownerUserId.join(',');
  if (f.source?.length) out.source = f.source.join(',');
  if (f.search) out.search = f.search;
  if (f.view) out.view = f.view;
  if (f.page) out.page = f.page;
  if (f.pageSize) out.pageSize = f.pageSize;
  if (f.sort) out.sort = f.sort;
  if (f.order) out.order = f.order;
  return out;
}

export function useLeadsList(filters: LeadListFilters) {
  return useQuery({
    queryKey: leadKeys.list(filters),
    queryFn: () =>
      callGet<LeadListResponse>('leads/list', serializeFilters(filters)),
  });
}

export function useLeadDetails(id: number | null | undefined) {
  return useQuery({
    enabled: id != null,
    queryKey: leadKeys.detail(id ?? 0),
    queryFn: () => callGet<LeadDetailsResponse>('leads/details', { id }),
  });
}

export function useAssignees(enabled = true) {
  return useQuery({
    enabled,
    queryKey: leadKeys.assignees,
    queryFn: () => callGet<{ users: LeadAssignee[] }>('leads/assignees'),
  });
}

const invalidateLists = () => {
  queryClient.invalidateQueries({ queryKey: leadKeys.lists() });
  queryClient.invalidateQueries({ queryKey: ['persons'] });
};
const invalidateDetail = (id: number) => {
  queryClient.invalidateQueries({ queryKey: leadKeys.detail(id) });
  queryClient.invalidateQueries({ queryKey: ['local-contacts', 'detail'] });
};

export function useCreateLead() {
  return useMutation({
    mutationFn: (body: Partial<Lead> & { source?: LeadSource }) =>
      callPost<{ lead: Lead; contact: LeadClient | null; existing?: boolean }>(
        'leads/create',
        body,
      ),
    onSuccess: invalidateLists,
  });
}

export function useMondayLeadImportPreview() {
  return useMutation({
    mutationFn: (mondayItemId: number) =>
      callGet<MondayLeadImportCandidate>('leads/import-monday-preview', {
        mondayItemId: String(mondayItemId),
      }),
  });
}

export function useImportMondayLead() {
  return useMutation({
    mutationFn: (mondayItemId: number) =>
      callPost<{ lead: Lead }>('leads/import-monday', { mondayItemId }),
    onSuccess: invalidateLists,
  });
}

export function useUpdateLead() {
  return useMutation({
    mutationFn: (body: Partial<Lead> & { id: number }) =>
      callPost<{ lead: Lead }>('leads/update', body),
    onSuccess: (_d, vars) => {
      invalidateLists();
      invalidateDetail(vars.id);
      if ('referringAgentId' in vars) {
        queryClient.invalidateQueries({ queryKey: referringAgentKeys.all });
      }
    },
  });
}

export function useUpdateLeadStatus() {
  return useMutation({
    mutationFn: (body: { id: number; status: LeadStatus }) =>
      callPost<{ lead: Lead }>('leads/update-status', body),
    onSuccess: (_d, vars) => {
      invalidateLists();
      invalidateDetail(vars.id);
    },
  });
}

export function useAssignLead() {
  return useMutation({
    mutationFn: (body: { leadId: number; assigneeUserId: number | null }) =>
      callPost<{ lead: Lead }>('leads/assign', body),
    onSuccess: (_d, vars) => {
      invalidateLists();
      invalidateDetail(vars.leadId);
    },
  });
}

export function useSetLastContact() {
  return useMutation({
    mutationFn: (body: { leadId: number; at?: string }) =>
      callPost<{ lead: Lead }>('leads/set-last-contact', body),
    onSuccess: (_d, vars) => {
      invalidateLists();
      invalidateDetail(vars.leadId);
    },
  });
}

export function useAddLeadComment() {
  return useMutation({
    mutationFn: (body: { leadId: number; body: string }) =>
      callPost<{ comment: LeadComment }>('leads/comment', body),
    onSuccess: (_d, vars) => invalidateDetail(vars.leadId),
  });
}

export function useDeleteLead() {
  return useMutation({
    mutationFn: (id: number) => callPost<{ ok: true }>('leads/delete', { id }),
    onSuccess: invalidateLists,
  });
}

export type QualifyLeadPayload = {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  whatsapp?: string | null;
  telegram?: string | null;
  notes?: string | null;
};

const afterQualify = (leadId: number) => {
  invalidateLists();
  invalidateDetail(leadId);
  queryClient.invalidateQueries({ queryKey: ['local-contacts'] });
};

export function useQualifyLead() {
  return useMutation({
    mutationFn: (body: {
      leadId: number;
      payload?: QualifyLeadPayload;
      accountId?: number | null;
    }) =>
      callPost<{
        contact: {
          id: number;
          firstName: string | null;
          lastName: string | null;
        };
        lead: Lead;
      }>('leads/qualify', body),
    onSuccess: (_d, vars) => afterQualify(vars.leadId),
  });
}

export function useTakeLead() {
  return useMutation({
    mutationFn: (body: { leadId: number; nextTaskAt?: string | null }) =>
      callPost<{ lead: Lead }>('leads/take', body),
    onSuccess: (_d, vars) => {
      invalidateLists();
      invalidateDetail(vars.leadId);
    },
  });
}

export function useDuplicateCheck(params: {
  email?: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
}) {
  const hasInput = Boolean(
    params.email || params.phone || params.firstName || params.lastName,
  );
  return useQuery({
    enabled: hasInput,
    queryKey: [...leadKeys.all, 'duplicate-check', params] as const,
    queryFn: () =>
      callGet<DuplicateCheckResult>('leads/duplicate-check', params),
    staleTime: 10_000,
  });
}

export function useRequestPersonAccess() {
  return useMutation({
    mutationFn: (body: { contactId: number; leadId: number }) =>
      callPost<{ notified: number }>('contacts/request-access', {
        contactId: body.contactId,
      }),
    onSuccess: (_d, vars) => invalidateDetail(vars.leadId),
  });
}

export function useAddPersonMember() {
  return useMutation({
    mutationFn: (body: { contactId: number; userId: number; leadId: number }) =>
      callPost<{ ok: true }>('contacts/add-member', {
        contactId: body.contactId,
        userId: body.userId,
      }),
    onSuccess: (_d, vars) => {
      invalidateDetail(vars.leadId);
      queryClient.invalidateQueries({ queryKey: ['local-contacts'] });
    },
  });
}
